import React from 'react';
import * as THREE from 'three';

const modulePalette = [0x4b6cff, 0xf1f0e9, 0x11120f, 0xb8ff35];

function createModule(index) {
  const module = new THREE.Group();
  const material = new THREE.MeshPhysicalMaterial({
    color: modulePalette[index % modulePalette.length],
    metalness: index % 3 === 0 ? 0.35 : 0.05,
    roughness: index % 2 === 0 ? 0.18 : 0.32,
    clearcoat: 1,
    clearcoatRoughness: 0.12,
  });
  const joint = new THREE.Mesh(new THREE.SphereGeometry(0.48, 24, 24), material);
  module.add(joint);

  const armGeometry = new THREE.CylinderGeometry(0.25, 0.34, 1.35, 20, 1, false);
  const capGeometry = new THREE.TorusGeometry(0.27, 0.095, 12, 24);
  const directions = [
    [0, 0.82, 0, 0, 0, 0],
    [0.72, -0.35, 0, 0, 0, Math.PI / 3],
    [-0.72, -0.35, 0, 0, 0, -Math.PI / 3],
  ];

  directions.forEach(([x, y, z, rx, ry, rz]) => {
    const arm = new THREE.Mesh(armGeometry, material);
    arm.position.set(x, y, z);
    arm.rotation.set(rx, ry, rz);
    module.add(arm);

    const cap = new THREE.Mesh(capGeometry, material);
    cap.position.set(x * 1.55, y * 1.55, z);
    cap.rotation.set(Math.PI / 2, 0, rz);
    module.add(cap);
  });

  module.userData.material = material;
  return module;
}

export default function InteractiveHeroScene() {
  const mountRef = React.useRef(null);

  React.useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x0a0b09, 0.035);

    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    camera.position.set(0, 0, 18);

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setClearColor(0x0a0b09, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;
    mount.appendChild(renderer.domElement);

    const world = new THREE.Group();
    scene.add(world);

    const modules = [];
    const moduleCount = window.innerWidth < 700 ? 13 : 22;
    for (let index = 0; index < moduleCount; index += 1) {
      const module = createModule(index);
      const angle = index * 2.39996;
      const radius = 1.1 + Math.sqrt(index) * 1.08;
      module.position.set(
        Math.cos(angle) * radius,
        Math.sin(angle) * radius * 0.72,
        (index % 5) * 0.62 - 1.5,
      );
      module.rotation.set(angle * 0.31, angle * 0.22, angle);
      const scale = 0.62 + (index % 4) * 0.11;
      module.scale.setScalar(scale);
      module.userData.origin = module.position.clone();
      module.userData.phase = index * 0.47;
      modules.push(module);
      world.add(module);
    }

    const particleGeometry = new THREE.BufferGeometry();
    const particleCount = window.innerWidth < 700 ? 80 : 160;
    const particlePositions = new Float32Array(particleCount * 3);
    for (let index = 0; index < particleCount; index += 1) {
      particlePositions[index * 3] = (Math.random() - 0.5) * 22;
      particlePositions[index * 3 + 1] = (Math.random() - 0.5) * 13;
      particlePositions[index * 3 + 2] = (Math.random() - 0.5) * 8;
    }
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    const particleMaterial = new THREE.PointsMaterial({ color: 0xb8ff35, size: 0.025, transparent: true, opacity: 0.55 });
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);

    scene.add(new THREE.HemisphereLight(0xe9edff, 0x11120f, 2.1));
    const keyLight = new THREE.DirectionalLight(0xffffff, 5.5);
    keyLight.position.set(5, 6, 8);
    scene.add(keyLight);
    const blueLight = new THREE.PointLight(0x4b6cff, 90, 24);
    blueLight.position.set(-5, 1, 5);
    scene.add(blueLight);
    const limeLight = new THREE.PointLight(0xb8ff35, 65, 18);
    limeLight.position.set(5, -3, 3);
    scene.add(limeLight);

    const pointer = new THREE.Vector2();
    const targetPointer = new THREE.Vector2();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let scrollProgress = 0;
    let frameId;

    const updateSize = () => {
      const { width, height } = mount.getBoundingClientRect();
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      world.scale.setScalar(width < 700 ? 0.72 : Math.min(1, width / 900));
      world.position.x = width < 800 ? 0 : 1.8;
    };

    const updatePointer = (event) => {
      targetPointer.x = (event.clientX / window.innerWidth) * 2 - 1;
      targetPointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    const updateScroll = () => {
      scrollProgress = Math.min(window.scrollY / Math.max(window.innerHeight, 1), 1.5);
    };

    const timer = new THREE.Timer();
    const animate = () => {
      timer.update();
      const elapsed = timer.getElapsed();
      pointer.lerp(targetPointer, 0.035);
      world.rotation.y += 0.0018;
      world.rotation.x = pointer.y * 0.13 + scrollProgress * 0.28;
      world.rotation.z = pointer.x * -0.08 + scrollProgress * 0.18;
      world.position.y = -scrollProgress * 2.1;
      camera.position.x += (pointer.x * 0.75 - camera.position.x) * 0.025;
      camera.position.y += (pointer.y * 0.45 - camera.position.y) * 0.025;
      camera.lookAt(0, -scrollProgress * 0.5, 0);

      modules.forEach((module) => {
        const wave = Math.sin(elapsed * 0.75 + module.userData.phase);
        module.position.z = module.userData.origin.z + wave * 0.32 + scrollProgress * module.userData.origin.x * 0.16;
        module.rotation.x += 0.0015;
        module.rotation.z -= 0.001;
      });
      particles.rotation.y = elapsed * 0.018;
      renderer.render(scene, camera);
      if (!reduceMotion) frameId = window.requestAnimationFrame(animate);
    };

    updateSize();
    updateScroll();
    window.addEventListener('resize', updateSize);
    window.addEventListener('pointermove', updatePointer, { passive: true });
    window.addEventListener('scroll', updateScroll, { passive: true });
    animate();

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('pointermove', updatePointer);
      window.removeEventListener('scroll', updateScroll);
      modules.forEach((module) => {
        module.traverse((child) => child.geometry?.dispose());
        module.userData.material.dispose();
      });
      particleGeometry.dispose();
      particleMaterial.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, []);

  return <div className="interactive-hero-scene" ref={mountRef} aria-hidden="true" />;
}